import { TFunction } from 'i18next';

import { Action, ActionDetail, ActionPhase } from '../types/actionTypes';
import {
  getActionStatusLabel,
  isTerminalActionStatus,
  normalizeActionStatus,
  resolveActionDisplayStatus,
} from './actionStatusConfig';

export interface ActionProgressSummary {
  total: number;
  completed: number;
  failed: number;
  running: number;
  cancelled: number;
  pending: number;
  done: number;
  progressRatio: number;
}

const humanizeToken = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const toSafeCount = (value?: number | null): number => Math.max(0, value ?? 0);

const isRunningStatus = (status: string): boolean =>
  status === 'IN_PROGRESS' || status === 'STARTED' || status === 'RUNNING' || status === 'CANCELLING';

const isSuccessfulStatus = (status: string): boolean =>
  status === 'COMPLETED' || status === 'SUCCESS' || status === 'SUCCEEDED';

const isFailedTerminalStatus = (status: string): boolean =>
  status === 'FAILED'
  || status === 'FAILURE'
  || status === 'TIMED_OUT'
  || status === 'BLOCKED'
  || status === 'SKIPPED'
  || status === 'OUTCOME_UNCONFIRMED';

export const summarizeActionDetails = (
  details: ActionDetail[] = [],
  expectedTotal?: number | null
): ActionProgressSummary => {
  const normalized = details.map((detail) => normalizeActionStatus(detail.status).toUpperCase());
  const total = Math.max(expectedTotal ?? 0, details.length);
  const completed = normalized.filter(isSuccessfulStatus).length;
  const failed = normalized.filter(isFailedTerminalStatus).length;
  const running = normalized.filter((status) => isRunningStatus(status)).length;
  const cancelled = normalized.filter((status) => status === 'CANCELLED' || status === 'CANCELED').length;
  const pending = Math.max(total - completed - failed - running - cancelled, 0);
  const done = Math.min(completed + failed + cancelled, total);
  const safeTotal = total > 0 ? total : details.length;

  return {
    total: safeTotal,
    completed,
    failed,
    running,
    cancelled,
    pending,
    done,
    progressRatio: safeTotal > 0 ? Math.min(done / safeTotal, 1) : 0,
  };
};

export const summarizeActionProgress = (action: Pick<Action, 'status' | 'aggregateStatus' | 'actionDetails' | 'totalRtus' | 'successfulRtus' | 'failedRtus' | 'runningRtus' | 'cancelledRtus'>): ActionProgressSummary => {
  const detailSummary = summarizeActionDetails(action.actionDetails, action.totalRtus);
  const total = Math.max(detailSummary.total, toSafeCount(action.totalRtus));
  const hasConcreteDetails = (action.actionDetails?.length ?? 0) > 0;
  const topLevelCompleted = Math.max(detailSummary.completed, toSafeCount(action.successfulRtus));
  const topLevelFailed = Math.max(detailSummary.failed, toSafeCount(action.failedRtus));
  const topLevelRunning = Math.max(detailSummary.running, toSafeCount(action.runningRtus));
  const topLevelCancelled = Math.max(detailSummary.cancelled, toSafeCount(action.cancelledRtus));
  const currentStatus = normalizeActionStatus(resolveActionDisplayStatus(action)).toUpperCase();
  const currentIsTerminal = isTerminalActionStatus(currentStatus);
  const detailLooksActive = detailSummary.running > 0 || detailSummary.pending > 0;
  const topLevelLooksTerminal = topLevelFailed > 0 || topLevelCompleted > 0 || topLevelCancelled > 0;
  const shouldTrustTopLevel = currentIsTerminal && topLevelLooksTerminal && detailLooksActive;

  const completed = shouldTrustTopLevel
    ? topLevelCompleted
    : hasConcreteDetails
      ? detailSummary.completed
      : topLevelCompleted;
  const failed = shouldTrustTopLevel
    ? topLevelFailed
    : hasConcreteDetails
      ? detailSummary.failed
      : topLevelFailed;
  const running = shouldTrustTopLevel
    ? toSafeCount(action.runningRtus)
    : hasConcreteDetails
      ? detailSummary.running
      : topLevelRunning;
  const cancelled = shouldTrustTopLevel
    ? topLevelCancelled
    : hasConcreteDetails
      ? detailSummary.cancelled
      : topLevelCancelled;
  const pending = Math.max(total - completed - failed - running - cancelled, 0);
  const done = Math.min(completed + failed + cancelled, total);

  return {
    total,
    completed,
    failed,
    running,
    cancelled,
    pending,
    done,
    progressRatio: total > 0 ? Math.min(done / total, 1) : 0,
  };
};

const STATUS_COUNT_ORDER: Array<{ key: keyof Pick<ActionProgressSummary, 'running' | 'pending' | 'failed' | 'cancelled' | 'completed'>; status: string }> = [
  { key: 'running', status: 'RUNNING' },
  { key: 'pending', status: 'QUEUED' },
  { key: 'failed', status: 'FAILED' },
  { key: 'cancelled', status: 'CANCELLED' },
  { key: 'completed', status: 'COMPLETED' },
];

/**
 * Renders the non-zero status buckets of a progress summary as a short line
 * ("2 running · 3 queued"), so an aggregate status like "Running" shown
 * above still-queued targets reads as an intentional in-flight state rather
 * than a contradiction.
 */
export const formatActionStatusCounts = (summary: ActionProgressSummary, t: TFunction): string =>
  STATUS_COUNT_ORDER
    .map(({ key, status }) => ({ count: summary[key], label: getActionStatusLabel(status, t) }))
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label.toLowerCase()}`)
    .join(' · ');

export const formatActionPhaseLabel = (phase: ActionPhase | undefined, t: TFunction): string => {
  const normalizedPhase = String(phase ?? 'INITIALIZED').trim().toUpperCase();

  switch (normalizedPhase) {
    case 'INITIALIZED':
      return t('actionProgress.phases.initialized', { defaultValue: 'Queued' });
    case 'LOGIN':
      return t('actionProgress.phases.login', { defaultValue: 'Connecting to device' });
    case 'PREPARING_COMMAND':
      return t('actionProgress.phases.preparingCommand', { defaultValue: 'Preparing command' });
    case 'DISPATCHING':
      return t('actionProgress.phases.dispatching', { defaultValue: 'Sending command to RTU' });
    case 'UPLOAD_CONFIG':
      return t('actionProgress.phases.uploadConfig', { defaultValue: 'Uploading config' });
    case 'UPLOAD_DEPLOY':
      return t('actionProgress.phases.uploadDeploy', { defaultValue: 'Uploading deployment' });
    case 'UPLOAD_LICENSE':
      return t('actionProgress.phases.uploadLicense', { defaultValue: 'Uploading license' });
    case 'DOWNLOAD_CONFIG':
      return t('actionProgress.phases.downloadConfig', { defaultValue: 'Downloading config' });
    case 'DOWNLOAD_DEPLOY':
    case 'DOWNLOAD_DEPLOYMENT':
      return t('actionProgress.phases.downloadDeploy', { defaultValue: 'Downloading deployment' });
    case 'REBOOT':
      return t('actionProgress.phases.reboot', { defaultValue: 'Restarting device' });
    case 'RTU_INFO':
      return t('actionProgress.phases.rtuInfo', { defaultValue: 'Requesting RTU information' });
    case 'RTU_DEBUG_LOG':
      return t('actionProgress.phases.rtuDebugLog', { defaultValue: 'Preparing RTU debug log' });
    case 'OUTCOME_UNCONFIRMED':
      return t('actionProgress.phases.outcomeUnconfirmed', { defaultValue: 'Waiting for a confirmed outcome' });
    default:
      return humanizeToken(normalizedPhase);
  }
};

export const formatActionProgressSummary = (
  summary: Pick<ActionProgressSummary, 'done' | 'total' | 'progressRatio'>,
  t: TFunction
): string => {
  if (summary.total <= 0) {
    return t('actionProgress.summary.noTargets', { defaultValue: 'Waiting for device progress' });
  }

  return t('actionProgress.summary.withPercent', {
    done: summary.done,
    total: summary.total,
    percent: Math.round(summary.progressRatio * 100),
    defaultValue: '{{done}} of {{total}} completed ({{percent}}%)',
  });
};
